import { COUNTRIES, CURRENCIES } from '../../../../shared/CountriesData';
import HorizontalLine from '../ui/HorizontalLine';
import StyledFormSection from '../styled/StyledFormSection';
import StyledInput from '../styled/StyledInput';
import MarginRateStyledInput from '../styled/MarginRateStyledInput';
import StyledSelect from '../styled/StyledSelect';
import { Label } from '../ui/label';
import ContactsSection from './ContactsSection';
import StyledDropZone from '../styled/StyledDropZone';
import StyledRadioGroup, {
  YesNoStyledRadioGroup,
} from '../styled/StyledRadioGroup';
import ChildAgeDefinition from './ChildAgeDefinition';
import HotelOtherPensionTypesPrices from './HotelCommonOtherPensionTypesPrices';
import HotelSupplements from './HotelSupplements';
import ExpertModeToggle from './excelPaste/ExpertModeToggle';
import { EXPERT_FORM_COLUMN_WIDTH } from './excelPaste/expertLayout';
import { useExpertMode } from './excelPaste/useExpertMode';
import {
  Control,
  useController,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useCallback, useMemo, useState } from 'react';
import { getRoomPensionOptions } from '@/constants/hotelPension';
import { HOTEL_CATEGORIES } from '@/constants/HotelCategories';
import { useGetBenefitsCities } from '@/hooks/useBenefits';
import { useGetChains } from '@/hooks/useHotel';
import { LabelValue } from '@/types/global';
import { BENEFIT_MARGIN_RATE_LABEL } from '@/constants/marginRateLabels';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableTextarea from '../styled/StyledTranslatableTextarea';
import TranslateIcon from '../translation/TranslateIcon';
import HotelAssociatedBenefitsSection from './HotelAssociatedBenefitsSection';

interface HotelConfigurationStepProps {
  control: Control<HotelFormDataWithTranslations>;
  suppliers?: {
    id: string;
    name: string;
    currency: string;
  }[];
  language: SupportedLocale;
  isViewMode?: boolean;
  errorsLength: number;
  translateFields: (
    fieldsToTranslate?: Array<
      | 'benefit.name'
      | 'shortDescriptionTitle'
      | 'shortDescriptionBody'
      | 'longDescriptionTitle'
      | 'longDescriptionBody'
    >,
  ) => Promise<void>;
  isTranslating: boolean;
}

const HotelConfigurationStep = ({
  control,
  suppliers,
  language,
  errorsLength,
  isViewMode = false,
  translateFields,
  isTranslating,
}: HotelConfigurationStepProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const selectedCountry = useWatch({ control, name: 'country' });

  const { clearErrors, setValue } =
    useFormContext<HotelFormDataWithTranslations>();
  const {
    field: { onChange: onChangeSupplier },
  } = useController({ control, name: 'supplier' });
  const {
    field: { onChange: onChangeCurrency },
  } = useController({ control, name: 'currency' });

  const { data: hotelCities } = useGetBenefitsCities({
    country: selectedCountry?.value,
    select: (data) => data.map(({ city }) => ({ label: city, value: city })),
  });
  const { data: chains } = useGetChains((data) =>
    data.map(({ chain }) => ({ label: chain, value: chain })),
  );

  const acceptsChildPricing = useWatch({
    control,
    name: 'acceptsChildPricing',
  });

  const pricingPensionType = useWatch({ control, name: 'pricingPensionType' });

  const shouldFillOtherPensionTypes = useWatch({
    control,
    name: 'shouldFillOtherPensionTypes',
  });
  const areOtherPensionPricesCommon = useWatch({
    control,
    name: 'areOtherPensionPricesCommon',
  });
  const shouldFillHotelSupplements = useWatch({
    control,
    name: 'shouldFillHotelSupplements',
  });
  const pricingType = useWatch({ control, name: 'pricingType' });
  const isExpertMode = useExpertMode() && !isViewMode;

  const reducedPriceExists = useWatch({ control, name: 'reducedPriceExists' });

  const [customChainsOptions, setCustomChainsOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [customCityOptions, setCustomCityOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const chainsOptions = chains
    ? [...chains, ...customChainsOptions]
    : customChainsOptions;
  const cityOptions = hotelCities
    ? [...hotelCities, ...customCityOptions]
    : customCityOptions;

  const suppliersOptions = useMemo(
    () => suppliers?.map(({ id, name }) => ({ value: id, label: name })) ?? [],
    [suppliers],
  );

  const [hotelCategoriesOptions, setHotelCategoriesOptions] = useState(
    getTranslatedConst(HOTEL_CATEGORIES).map((category) => ({
      value: category,
      label: category,
    })),
  );

  const roomPensionTypesOptions = useMemo(() => getRoomPensionOptions(t), [t]);

  const onChangeSupplierValue = useCallback(
    (newValue: unknown) => {
      const value = newValue as LabelValue<string>;
      onChangeSupplier(value);
      const supplier = suppliers?.find(
        (supplier) => supplier.id === value.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.value === supplier?.currency,
      );
      if (currency) {
        onChangeCurrency(currency);
        clearErrors('currency');
      }
    },
    [clearErrors, onChangeCurrency, onChangeSupplier, suppliers],
  );

  return (
    <>
      <StyledFormSection
        sectionTitle={t('hotels.generalInfo')}
        icon={
          <TranslateIcon
            translateFields={() => translateFields(['benefit.name'])}
            isTranslating={isTranslating}
            language={language}
            hidden={isViewMode}
          />
        }
      >
        {errorsLength > 0 && (
          <Label className="text-error-500">
            {t('hotels.checkRequiredFields')}
          </Label>
        )}
        <StyledTranslatableInput
          control={control}
          name="benefit.name"
          originalName="name"
          setValue={setValue}
          language={language}
          title={t('hotels.name')}
          placeholder={t('hotels.namePlaceholder')}
          disabled={isViewMode}
        />
        <div className="flex flex-col md:flex-row gap-4">
          <StyledSelect
            options={hotelCategoriesOptions}
            control={control}
            name="category"
            title={t('hotels.category')}
            placeholder={t('hotels.selectCategory')}
            creatable
            onCreateOption={(option) =>
              setHotelCategoriesOptions([
                ...hotelCategoriesOptions,
                { label: option, value: option },
              ])
            }
            disabled={isViewMode}
          />
          <StyledSelect
            options={chainsOptions}
            control={control}
            name="chain"
            title={t('hotels.chain')}
            placeholder={t('hotels.selectChain')}
            creatable
            onCreateOption={(option) =>
              setCustomChainsOptions([
                ...customChainsOptions,
                { label: option, value: option },
              ])
            }
            disabled={isViewMode}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <StyledSelect
            options={suppliersOptions}
            control={control}
            name="supplier"
            title={t('hotels.supplier')}
            placeholder={t('hotels.selectSupplier')}
            disabled={isViewMode}
            onChange={onChangeSupplierValue}
          />
          <StyledSelect
            options={CURRENCIES}
            control={control}
            name="currency"
            title={t('hotels.currency')}
            placeholder={t('hotels.selectCurrency')}
            disabled={isViewMode}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <StyledSelect
            options={COUNTRIES}
            control={control}
            name="country"
            title={t('hotels.country')}
            placeholder={t('hotels.selectCountry')}
            disabled={isViewMode}
          />
          <MarginRateStyledInput
            control={control}
            name="marginRate"
            title={getTranslatedConst(BENEFIT_MARGIN_RATE_LABEL)}
            placeholder={t('hotels.addRate')}
            disabled={isViewMode}
          />
        </div>
        <Label className="text-gray-700">{t('hotels.requiredFields')}</Label>
      </StyledFormSection>
      <HorizontalLine />
      <StyledFormSection sectionTitle={t('hotels.address')}>
        <StyledInput
          control={control}
          name="address"
          title={t('hotels.address')}
          placeholder={t('hotels.addAddress')}
          disabled={isViewMode}
        />
        <div className="flex flex-col md:flex-row gap-4">
          <StyledInput
            control={control}
            name="postalCode"
            type="number"
            min={0}
            title={t('hotels.postalCode')}
            placeholder={t('hotels.addPostalCode')}
            hideNumberFieldArrows
            disabled={isViewMode}
          />
          <StyledSelect
            options={cityOptions}
            control={control}
            name="city"
            title={t('hotels.city')}
            placeholder={t('hotels.addCity')}
            creatable
            onCreateOption={(option) =>
              setCustomCityOptions([
                ...customCityOptions,
                { label: option, value: option },
              ])
            }
            disabled={isViewMode}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <StyledInput
            control={control}
            name="latitude"
            type="number"
            title={t('hotels.latitude')}
            placeholder={t('hotels.addLatitude')}
            min={-90}
            max={90}
            hideNumberFieldArrows
            disabled={isViewMode}
          />
          <StyledInput
            control={control}
            name="longitude"
            type="number"
            title={t('hotels.longitude')}
            placeholder={t('hotels.addLongitude')}
            min={-180}
            max={180}
            hideNumberFieldArrows
            disabled={isViewMode}
          />
        </div>
      </StyledFormSection>
      <HorizontalLine />
      <ContactsSection control={control} isViewMode={isViewMode} />
      <HorizontalLine />
      <StyledFormSection
        sectionTitle={t('hotels.description')}
        icon={
          <TranslateIcon
            translateFields={() =>
              translateFields([
                'shortDescriptionTitle',
                'shortDescriptionBody',
                'longDescriptionTitle',
                'longDescriptionBody',
              ])
            }
            isTranslating={isTranslating}
            language={language}
            hidden={isViewMode}
          />
        }
      >
        <StyledDropZone
          control={control}
          name="mainImage"
          title={t('hotels.mainImage')}
          placeholder={t('hotels.mainImagePlaceholder')}
          disabled={isViewMode}
        />
        <StyledDropZone
          control={control}
          name="images"
          title={t('hotels.diaporama')}
          placeholder={t('hotels.mainImagePlaceholder')}
          multiple
          disabled={isViewMode}
        />
        <div className="flex flex-col">
          <StyledTranslatableInput
            control={control}
            name="shortDescriptionTitle"
            originalName="shortDescriptionTitle"
            setValue={setValue}
            language={language}
            title={t('hotels.shortDescriptionTitle')}
            placeholder={t('hotels.shortDescriptionTitlePlaceholder')}
            disabled={isViewMode}
          />
          <StyledTranslatableTextarea
            control={control}
            name="shortDescriptionBody"
            originalName="shortDescriptionBody"
            setValue={setValue}
            language={language}
            placeholder={t('hotels.shortDescriptionBodyPlaceholder')}
            disabled={isViewMode}
          />
        </div>
        <div className="flex flex-col">
          <StyledTranslatableInput
            control={control}
            name="longDescriptionTitle"
            originalName="longDescriptionTitle"
            setValue={setValue}
            language={language}
            title={t('hotels.longDescriptionTitle')}
            placeholder={t('hotels.longDescriptionTitlePlaceholder')}
            disabled={isViewMode}
          />
          <StyledTranslatableTextarea
            control={control}
            name="longDescriptionBody"
            originalName="longDescriptionBody"
            setValue={setValue}
            language={language}
            placeholder={t('hotels.longDescriptionBodyPlaceholder')}
            disabled={isViewMode}
          />
        </div>
      </StyledFormSection>
      <HorizontalLine />
      <StyledFormSection
        sectionTitle={t('hotels.complementaryInfo')}
        contentWidthClassName={
          isExpertMode ? EXPERT_FORM_COLUMN_WIDTH : undefined
        }
      >
        {!isViewMode && <ExpertModeToggle />}
        <h2 className="text-base font-medium text-black">
          {t('hotels.pricing')}
        </h2>
        <div className="flex flex-col gap-2">
          <Label className="text-gray-700">{t('hotels.pricingType')}</Label>
          <StyledRadioGroup
            control={control}
            name={`pricingType`}
            options={[
              { label: t('hotels.byPerson'), value: 'byPerson' },
              { label: t('hotels.byRoom'), value: 'byRoom' },
            ]}
            disabled={isViewMode}
          />
        </div>
        {pricingType === 'byPerson' && (
          <>
            <div className="flex flex-col gap-2">
              <Label className="text-gray-700">
                {t('hotels.reducedPriceExists')}
              </Label>
              <YesNoStyledRadioGroup
                control={control}
                name={`reducedPriceExists`}
                disabled={isViewMode}
              />
            </div>
            {reducedPriceExists === 'true' && (
              <div className="flex flex-col gap-2">
                <Label className="text-gray-700">
                  {t('hotels.priceReductionType')}
                </Label>
                <StyledRadioGroup
                  control={control}
                  name={`priceReductionType`}
                  options={[
                    { label: t('hotels.newPrice'), value: 'newPrice' },
                    { label: t('hotels.reduction'), value: 'reduction' },
                  ]}
                  disabled={isViewMode}
                />
              </div>
            )}
          </>
        )}
        <h2 className="text-base font-medium text-black">
          {t('hotels.childReducedPrices')}
        </h2>
        <Label className="text-gray-700">
          {t('hotels.acceptsChildPricing')}
        </Label>
        <YesNoStyledRadioGroup
          control={control}
          name="acceptsChildPricing"
          disabled={isViewMode}
        />
        {acceptsChildPricing === 'true' && (
          <ChildAgeDefinition control={control} isViewMode={isViewMode} />
        )}
        <h2 className="text-base font-medium text-black">
          {t('hotels.pricingPension')}
        </h2>
        <StyledSelect
          options={roomPensionTypesOptions}
          control={control}
          name="pricingPensionType"
          title={t('hotels.pricingPensionType')}
          placeholder={t('hotels.selectPensionType')}
          disabled={isViewMode}
        />
        {pricingPensionType?.value === 'AllInclusive' ? null : (
          <>
            <Label className="text-gray-700">
              {t('hotels.shouldFillOtherPensionTypes')}
            </Label>
            <YesNoStyledRadioGroup
              control={control}
              name="shouldFillOtherPensionTypes"
              disabled={isViewMode}
            />
            {shouldFillOtherPensionTypes === 'true' && (
              <>
                <Label className="text-gray-700">
                  {t('hotels.areOtherPensionPricesCommon')}
                </Label>
                <YesNoStyledRadioGroup
                  control={control}
                  name="areOtherPensionPricesCommon"
                  disabled={isViewMode}
                />
                {areOtherPensionPricesCommon === 'true' && (
                  <HotelOtherPensionTypesPrices
                    control={control}
                    isViewMode={isViewMode}
                  />
                )}
              </>
            )}
          </>
        )}
        <span className="text-sm text-gray-700 font-medium leading-6 peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {t('hotels.shouldFillHotelSupplements')}
        </span>
        <YesNoStyledRadioGroup
          control={control}
          name="shouldFillHotelSupplements"
          disabled={isViewMode}
        />
        {shouldFillHotelSupplements === 'true' && (
          <HotelSupplements control={control} isViewMode={isViewMode} />
        )}
      </StyledFormSection>
      <HorizontalLine />
      <HotelAssociatedBenefitsSection
        control={control}
        isViewMode={isViewMode}
      />
    </>
  );
};

export default HotelConfigurationStep;
